import { Alert, Card, Heading, InfoTip, NumberField } from "@/design-system";
import { trackEvent } from "@/lib/dataLayer";
import { useMemo, useState } from "react";
import { BLOG_POSTS, CET_EXPLANATION, SITE_URL } from "../constants";
import { InstallmentSchedule } from "../components/installmentSchedule";
import { RelatedLinks } from "../components/relatedLinks";
import {
  type Simulation,
  SimulationSummary,
} from "../components/simulationSummary";
import { ToolPageLayout } from "../components/toolPageLayout";
import { ToolResultCta } from "../components/toolResultCta";
import {
  annualRate,
  formatCurrency,
  formatPercent,
  monthlyEffectiveCost,
  pricePayment,
  priceSchedule,
} from "../utils/finance";
import {
  DESCRIPTION,
  FAQ_ITEMS,
  PATH,
  TITLE,
  TRAIL,
  WEB_APPLICATION,
} from "./constants";

const MAX_INSTALLMENTS = 48;

export const SimuladorDeCrediario = () => {
  const [price, setPrice] = useState<number | undefined>(1500);
  const [downPayment, setDownPayment] = useState<number | undefined>(0);
  const [ratePercent, setRatePercent] = useState<number | undefined>(3);
  const [installments, setInstallments] = useState<number | undefined>(10);
  const [fee, setFee] = useState<number | undefined>(0);

  const financed = (price ?? 0) - (downPayment ?? 0);
  const downPaymentError =
    (price ?? 0) > 0 && financed <= 0
      ? "A entrada precisa ser menor que o valor à vista."
      : undefined;
  const installmentsError =
    !installments || installments < 1
      ? `Informe de 1 a ${MAX_INSTALLMENTS} parcelas.`
      : undefined;
  const isValid = (price ?? 0) > 0 && !downPaymentError && !installmentsError;

  const { simulation, schedule } = useMemo(() => {
    if (!isValid || !installments) return { simulation: null, schedule: [] };
    const rate = (ratePercent ?? 0) / 100;
    // A tarifa entra no saldo financiado e é paga junto com as parcelas.
    const principal = financed + (fee ?? 0);
    const payment = pricePayment(principal, rate, installments);
    const cet = monthlyEffectiveCost(financed, payment, installments);
    const result: Simulation = {
      installments,
      payment,
      downPayment: downPayment ?? 0,
      totalPaid: (downPayment ?? 0) + payment * installments,
      totalInterest: payment * installments - financed,
      monthlyRate: formatPercent(rate),
      yearlyRate: formatPercent(annualRate(rate)),
      monthlyCet: formatPercent(cet),
      yearlyCet: formatPercent(annualRate(cet)),
    };
    return {
      simulation: result,
      schedule: priceSchedule(principal, rate, installments),
    };
  }, [isValid, installments, ratePercent, financed, fee, downPayment]);

  const share = () => {
    if (!simulation) return;
    trackEvent({
      event: "tool_action",
      tool: "simulador_crediario",
      action: "whatsapp_share",
    });
    const lines = [
      "*Simulação do seu crediário*",
      `Valor à vista: ${formatCurrency(price ?? 0)}`,
      simulation.downPayment > 0
        ? `Entrada: ${formatCurrency(simulation.downPayment)}`
        : null,
      `${simulation.installments}x de ${formatCurrency(simulation.payment)}`,
      `Total a prazo: ${formatCurrency(simulation.totalPaid)}`,
      `Juros: ${simulation.monthlyRate} ao mês (${simulation.yearlyRate} ao ano)`,
      `CET: ${simulation.monthlyCet} ao mês (${simulation.yearlyCet} ao ano)`,
      "",
      `Simulado em ${SITE_URL.replace("https://", "")}${PATH}`,
    ].filter(line => line !== null);
    window.open(
      `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <ToolPageLayout
      title={TITLE}
      description={DESCRIPTION}
      trail={TRAIL}
      schemas={[WEB_APPLICATION]}
      eyebrow="Ferramenta grátis"
      heading="Simulador de crediário"
      lead="Calcule a parcela, os juros e o custo efetivo total de uma venda no crediário próprio, veja a tabela mês a mês e mande a simulação para o cliente no WhatsApp."
      faqItems={FAQ_ITEMS}
      faqHeading="Dúvidas sobre juros no crediário"
    >
      {openDemo => (
        <>
          <section
            aria-labelledby="simulador-title"
            className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-6 px-4 sm:px-6 lg:grid-cols-5 lg:px-8"
          >
            <Card className="flex min-w-0 flex-col gap-5 lg:col-span-2">
              <Heading level={2} size={4} id="simulador-title">
                Dados da venda
              </Heading>
              <NumberField
                label="Valor à vista"
                required
                prefix="R$"
                decimals={2}
                max={10_000_000}
                value={price}
                onValueChange={setPrice}
              />
              <NumberField
                label="Entrada"
                hint="Deixe zerado se a venda não tem entrada."
                prefix="R$"
                decimals={2}
                max={10_000_000}
                value={downPayment}
                onValueChange={setDownPayment}
                error={downPaymentError}
              />
              <NumberField
                label="Número de parcelas"
                required
                decimals={0}
                max={MAX_INSTALLMENTS}
                value={installments}
                onValueChange={setInstallments}
                error={installmentsError}
              />
              <NumberField
                label="Taxa de juros ao mês"
                required
                hint="Use 0 para parcelamento sem juros."
                suffix="%"
                decimals={2}
                max={30}
                value={ratePercent}
                onValueChange={setRatePercent}
              />
              <NumberField
                label="Tarifa de cadastro"
                hint={
                  <span className="inline-flex items-center gap-1">
                    Entra nas parcelas e aparece no CET.
                    <InfoTip label="O que é CET?">{CET_EXPLANATION}</InfoTip>
                  </span>
                }
                prefix="R$"
                decimals={2}
                max={100_000}
                value={fee}
                onValueChange={setFee}
              />
            </Card>

            <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
              {simulation ? (
                <SimulationSummary simulation={simulation} onShare={share} />
              ) : (
                <Alert tone="info" title="Complete os dados da venda">
                  Informe o valor à vista e o número de parcelas para ver a
                  simulação.
                </Alert>
              )}
              <Alert tone="info">
                Valores de referência pela Tabela Price, sem IOF. Confira a taxa
                e as tarifas do seu contrato antes de fechar a venda.
              </Alert>
            </div>
          </section>

          {schedule.length > 0 ? (
            <section className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 sm:px-6 lg:px-8">
              <InstallmentSchedule rows={schedule} />
              <Alert
                tone="info"
                title="Por que os juros diminuem a cada parcela?"
              >
                Na Tabela Price, os juros de cada mês são calculados sobre o
                saldo que o cliente ainda deve. Como a parcela é fixa, o saldo
                cai mês a mês: os juros diminuem e uma parte maior da parcela
                passa a abater a dívida.
              </Alert>
            </section>
          ) : null}

          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
            <ToolResultCta
              id="simulador-cta-title"
              title="Depois da venda, a Fidexa cobra cada parcela por você"
              description="Lembrete antes do vencimento, cobrança no atraso e acordo de parcelamento, tudo pelo WhatsApp e sem planilha."
              onDemo={openDemo}
            />
          </div>

          <RelatedLinks
            current="simulador_crediario"
            posts={[BLOG_POSTS.jurosCrediario, BLOG_POSTS.reguaAutomatica]}
          />
        </>
      )}
    </ToolPageLayout>
  );
};
